import { Component, OnInit, ChangeDetectorRef, Input, OnDestroy, Output, EventEmitter, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { franc } from 'franc';
import { LoggerService } from 'src/services/logger-services/logger.service';
/**
 * Component responsible for handling audio playback (text-to-speech) and copying chatbot answers to the clipboard.
 * 
 * Inputs:
 * - `chatdata`: Object containing the question and answer to speak/copy.
 * - `ChatQueriesList`: List of all chat query objects to manage concurrent speech states.
 */
@Component({
  selector: 'app-audio-copy',
  templateUrl: './audio-copy.component.html',
  styleUrls: ['./audio-copy.component.css']
})
export class AudioCopyComponent implements OnInit, OnDestroy {

  /** Chat entry containing question and answer data. */
  @Input() chatdata: any;
  
  /** Rendered answer HTML generated by the markdown sanitizer pipe. */
  @Input() renderedAnswerHtml: SafeHtml | string | null = null;

  /** List of all chat queries for controlling simultaneous speech. */
  @Input() ChatQueriesList: any;
  
  /** Emits the updated ChatQueriesList*/
  @Output() ChatQueriesListEmit= new EventEmitter<any>();

  /** Whether text-to-speech is currently playing. */
  isSpeaking: boolean = false;

  /** Whether text-to-speech is currently paused. */
  isPaused: boolean = false;

  /** Indicates if the answer has been successfully copied. */
  isAnswerCopied: boolean = false;

  /** Current SpeechSynthesisUtterance being spoken. */
  utterance: SpeechSynthesisUtterance | null = null;

  /** Reference to the browser's speech synthesis engine. */
  synth = window.speechSynthesis;

  /** Error template for READ ALOUD */
  private readonly readAloudError = (error: any) => 
    `[READ ALOUD] - ${JSON.stringify(error)}`;

  /**
   * Handles cross-tab speech cancellation.
   * The `storage` event only fires in OTHER browser tabs, so this naturally
   * avoids cancelling speech in the tab that initiated the new speech request.
   */
  private readonly onCrossTabSpeechCancel = (event: StorageEvent): void => {
    if (event.key === 'freya-speech-cancel') {
      try {
        this.synth.cancel();
        this.utterance = null;
        this.isSpeaking = false;
        this.isPaused = false;
        if (this.chatdata) {
          this.chatdata.isSpeaking = false;
          this.chatdata.isPaused = false;
          this.chatdata.utterance = null;
        }
        this.ChatQueriesList?.forEach((item: any) => {
          item.isSpeaking = false;
          item.isPaused = false;
        });
        this.ChatQueriesListEmit.emit(this.ChatQueriesList);
        this.cdr.detectChanges();
      } catch (e) {
        this.loggerService.logError(this.readAloudError(e), 'onCrossTabSpeechCancel');
      }
    }
  };

  /**
   * Constructor injecting Angular's ChangeDetectorRef for manual change detection.
   * @param cdr - ChangeDetectorRef instance
   */
  constructor(public cdr: ChangeDetectorRef,
    public loggerService: LoggerService,
    private readonly domSanitizer: DomSanitizer
  ) {}

  /**
   * Initializes component state.
   */
  ngOnInit(): void {
    this.isSpeaking = false;
    this.isAnswerCopied = false;
    this.isPaused = false;
    window.addEventListener('storage', this.onCrossTabSpeechCancel);
  }
  

  /**
   * Cleans up speech synthesis on component destroy to prevent leaks or unfinished speech.
   */
  ngOnDestroy(): void {
    this.stopSpeaking();
    window.removeEventListener('storage', this.onCrossTabSpeechCancel);
  }

  /**
   * Strips HTML tags from a given string and returns plain text.
   * 
   * @param html - The HTML string to be stripped.
   * @returns The plain text without HTML tags.
   */
  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }


  /**
   * Detects the language of the given text using franc for wide multilingual support.
   *
   * @param text - The input text to analyze.
   * @returns A short language code: 'en', 'jp', 'zh', or fallback 'en' if detection fails.
   */
  detectLanguageFromText(text: string): 'en' | 'jp' | 'zh-CN' {
    const hasJapanese = /[\u3040-\u30FF\u31F0-\u31FF\uFF66-\uFF9D]/.test(text);
    if (hasJapanese) return 'jp';

    const hasChinese = /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(text);
    if (hasChinese) return 'zh-CN';

    const langCode = franc(text, { minLength: 5 }); // franc needs enough characters   
    // Map franc's ISO 639-3 codes to your supported language codes
    const francToShortLang: { [key: string]: 'en' | 'jp' | 'zh-CN' } = {
      'eng': 'en',
      'jpn': 'jp',
      'cmn': 'zh-CN', // Mandarin Chinese
      'zho': 'zh-CN', // Generic Chinese
    };
    return francToShortLang[langCode] ?? 'en';
    }


  /**
   * Processes the chat data to apply various text transformations.
   * 
   * @param chatdata - The chat object containing the answer to process.
   * @returns The processed text with all transformations applied.
   */
    processChatData(chatdata: any): string {
      try {
        let textWithMarkers = this.convertRomanNumerals(chatdata.answer)
          // Remove URLs - Safe regex with bounded quantifiers
          .replace(/(?:^|\s)(https?:\/\/|www\.|mailto:)[^\s]{0,200}/gim, '')
          // Replace markdown links - Safe regex with bounded quantifiers
          .replace(/\[([^\]\r\n]{1,100})\]\((https?:\/\/[^\s)]{1,200})\)/g, '$1')
          // Replace URLs - Safe regex with bounded quantifiers
          .replace(/(https?:\/\/[^\s]{1,200})/g, (match:string) => {
            return match
              .replace(/slash/g, '/')
              .replace(/\[PAUSE_LONG\]/g, '.')
              .replace(/\[PAUSE_SHORT\]/g, '');
          });
        
        textWithMarkers = textWithMarkers
          // Convert <br><br> to a long pause - Safe regex with bounded quantifiers
          .replace(/<br(?:\s{0,3}\/?)>(?:\s{0,3})<br(?:\s{0,3}\/?)>/gi, '[PAUSE_LONG]')
          // Add short pause after numbered points (e.g., 1. Step) - Safe regex
          .replace(/(\d{1,10})\.(?=\s)/g, '$1[PAUSE_SHORT]')
          // Remove bullet points - Safe regex with bounded quantifier
          .replace(/•\s{0,100}/g, '')
          // Replace common sentence delimiters with pauses - Safe regex
          .replace(/;/g, '[PAUSE_SHORT]')
          .replace(/:/g, '[PAUSE_SHORT]')
          // Replace "etc." with "etecetra" - Safe regex
          .replace(/\betc\.\s*/gi, 'etecetra ')
          // Convert "model no." → "model number" - Safe regex with bounded quantifiers
          .replace(/\b(\w{1,50})\s+no\.\b\s*/gi, '$1 number ')
          // Remove stray commas after bullet points - Safe regex with bounded quantifiers
          .replace(/•\s*([^,\n]{1,100}),/g, '.. $1')
          // Normalize spacing after bullet - Safe regex with bounded quantifier
          .replace(/•\s{0,100}/g, '.')
          // Remove horizontal lines made of dashes - Safe regex
          .replace(/^-{3,}$/gm, '')
          // Replace slashes with "slash" - Safe regex with bounded quantifiers
          .replace(/\s{0,100}\/\s{0,100}/g, ' slash ')
          // Remove markdown headers - Safe regex with bounded quantifiers
          .replace(/^#+\s*([^\n]{1,100})$/gm, '$1')
          // Replace "e.g." with "example" - Safe regex
          .replace(/\be\.g\.\s*/gi, 'example ')
          // Spell out words in brackets - Safe regex with bounded quantifiers
          .replace(/\[([A-Z\s]{1,200})\]/g, (match: string, p1:string) => {
            return p1.split(' ').map((word: string) => word.split('').join(' ')).join(' '); // Spell out each word
          });
        
        let cleanText = this.removeNonResponsiveCharacters(this.stripHtml(textWithMarkers.replace(/\./g, '[PAUSE_LONG]')));
        return cleanText;
    } catch (error) {
      this.loggerService.logError(this.readAloudError(error), "processChatData");
      return '';
    }
  }


  /**
   * Retrieves the most suitable female voice for the given short language code (`en`, `jp`, `zh`).
   * It prioritizes voices that match both the language and known female-sounding voice names.
   * Falls back to any female voice, or the first available voice if no match is found.
   *
   * @param language - The short language code (e.g., 'en', 'jp', 'zh').
   * @returns The best matching SpeechSynthesisVoice or null if no voices are available.
 */
  getPreferredVoice(language: string): SpeechSynthesisVoice | null {
    try {
      const voices = this.synth.getVoices();
      if (!voices.length) {
        this.loggerService.logError(this.readAloudError('No voices loaded.'), "getPreferredVoice");
        return null;
      }

      const langMap: { [key: string]: string } = {
        'en': 'en-US',
        'jp': 'ja-JP',
        'zh': 'zh-CN',
        'zh-CN': 'zh-CN'
      };

      const normalizedLang = langMap[language] || 'en-US';

      const preferredVoicesByLang: { [lang: string]: string[] } = {
        'en-US': ['Samantha', 'Zira', 'Google US English'],
        'ja-JP': ['Kyoko', 'Google 日本語'],
        'zh-CN': ['Ting-Ting', 'Mei-Jia', 'Google 普通话']
      };

      const preferredNames = preferredVoicesByLang[normalizedLang] || [];

      // Try preferred female voice names first
      const preferred = voices.find(v =>
        v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()) &&
        preferredNames.some(name => v.name.toLowerCase().includes(name.toLowerCase()))
      );

      if (preferred) return preferred;

      // Try any voice matching lang and sounds female
      const femaleFallback = voices.find(v =>
        v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()) &&
        /female|samantha|zira|kyoko|ting|mei|xiao|mizuki|hana/.test(v.name.toLowerCase())
      );

      if (femaleFallback) return femaleFallback;

      // Final fallback: any voice with the matching language
      const langFallback = voices.find(v => v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()));

      if (langFallback) return langFallback;

      if (voices.length > 0) return voices[0];
    } catch (error) {
      this.loggerService.logError(this.readAloudError(
        `No valid voice found for the specified language: ${language}`), 
        "getPreferredVoice");
      return null;
    }
    return null
  }

  
  /**
   * Converts `chatdata.answer` to speech using a human-like female voice with natural pacing and pauses.
   * Also ensures only one response plays at a time from the chat list.
   * 
   * @param chatdata - The chat object to speak from
   * @param ChatQueriesList - All chat queries to manage multiple utterances
   */
  speakResponse(chatdata: any, ChatQueriesList: any): void {
    try {
    if (!window.speechSynthesis) {
      this.loggerService.logError(this.readAloudError('Speech synthesis not supported in this browser.'), "speakResponse");
      return;
    }

    // Stop speech from other chats
    ChatQueriesList.forEach((item: any) => {
      if (item !== chatdata && (item.isSpeaking || item.isPaused)) {
        this.stopSpeaking();
      }
    });

    // Toggle pause/resume
    if (chatdata.isSpeaking) {
      if (chatdata.isPaused) {
        this.synth.resume();
        chatdata.isPaused = false;
      } else {
        this.synth.pause();
        chatdata.isPaused = true;
      }
      this.updateChatQueriesListSpeakingStatus(chatdata);
      // Emit the updated ChatQueriesList
      this.ChatQueriesListEmit.emit(this.ChatQueriesList);
      this.cdr.detectChanges();
      return;
    }

    // Clean HTML and prepare for segmenting
    let textWithMarkers = this.processChatData(chatdata);

    let segments = textWithMarkers.split(/(\[PAUSE_SHORT\]|\[PAUSE_LONG\])/);

    const speakSegments = (index = 0) => {
      if (index >= segments.length) {
        chatdata.isSpeaking = false;
        chatdata.isPaused = false;
        chatdata.utterance = null;
        this.cdr.detectChanges();
        return;
      }

      const segment = segments[index];
      let delay = 0;

      if (segment === '[PAUSE_SHORT]') {
        delay = 800;
      } else if (segment === '[PAUSE_LONG]') {
        delay = 1000;
      } else if (segment.trim()) {
        const utterance = new SpeechSynthesisUtterance(segment.trim());
        const detectedLang = this.detectLanguageFromText(segment.trim());
        const langMap: { [key: string]: string } = { 'en': 'en-US', 'jp': 'ja-JP', 'zh-CN': 'zh-CN' };
        const normalizedLang = langMap[detectedLang] || 'en-US';

        const voice = this.getPreferredVoice(detectedLang);
        if (!voice) {
          this.loggerService.logError(`No valid voice found for the specified language ${detectedLang}`, "speakResponse");
          return; // Exit if no valid voice is found
        }
        try {
          utterance.voice = voice;
        } catch (err) {
          this.loggerService.logError(`Failed to set voice on utterance: ${JSON.stringify(err)}`, "speakResponse");
        }
        utterance.lang = normalizedLang;

        utterance.pitch = 1.1;
        utterance.rate = 0.9; 
        utterance.volume = 0.9;

        utterance.onstart = () => {
          chatdata.isSpeaking = true;
          chatdata.isPaused = false;
          this.cdr.detectChanges();
        };

        utterance.onend = () => {
          setTimeout(() => speakSegments(index + 1), 0);
        };

        this.synth.speak(utterance);
        return;
      }

      setTimeout(() => speakSegments(index + 1), delay);
    };

      // Signal other browser tabs to cancel their speech so the OS engine is released
      try {
        localStorage.setItem('freya-speech-cancel', Date.now().toString());
      } catch (e) {
        this.loggerService.logError(this.readAloudError(e), 'speakResponse:crossTabSignal');
      }

      this.synth.cancel();
      // Delay after cancel() to let the OS speech engine fully release —
      // calling speak() immediately after cancel() silently fails in Chrome/Edge
      setTimeout(() => {
        try {
          speakSegments();
          this.ChatQueriesListEmit.emit(this.ChatQueriesList);
        } catch (error) {
          this.loggerService.logError(this.readAloudError(error), "speakResponse");
          this.stopSpeaking();
          if (chatdata) {
            chatdata.isSpeaking = false;
            chatdata.isPaused = false;
            chatdata.utterance = null;
          }
        }
      }, 200);
    } catch (error) {
      this.loggerService.logError(this.readAloudError(error), "speakResponse");
      this.stopSpeaking();
      // Reset chatdata state specifically
      if (chatdata) {
        chatdata.isSpeaking = false;
        chatdata.isPaused = false;
        chatdata.utterance = null;
      }
      return;
    }
  }

  /**
   * Stops any ongoing speech synthesis and resets state.
   */
  stopSpeaking(): void {
    try{
      this.synth.cancel();
      this.utterance = null;
      this.isSpeaking = false;
      this.isPaused = false;

      // Update the speaking status for all items in ChatQueriesList
      this.ChatQueriesList.forEach((item: any) => {
          if (item !== this.chatdata) {
              item.isSpeaking = false; // Set isSpeaking to false for other items
              item.isPaused = false;   // Optionally reset isPaused as well
          }
      });

      this.ChatQueriesListEmit.emit(this.ChatQueriesList);
    } catch (error) {
      this.loggerService.logError(this.readAloudError(error), "stopSpeaking");
    }
}

  updateChatQueriesListSpeakingStatus(chatdata: any): void {
    this.ChatQueriesList.forEach((item: any) => {
        if (item === chatdata) {
            item.isSpeaking = true; // Update isSpeaking to true for the matching item
        } else {
            item.isSpeaking = false; // Optionally set isSpeaking to false for others
        }
    });
  }

  /**
   * Copies the rendered answer content to clipboard.
   * Uses rendered HTML to produce plain text matching on-screen formatting.
   */
  async copyToClipboard(): Promise<void> {
    const renderedHtml = this.getRenderedHtmlForCopy();
    const sourceText = renderedHtml || this.chatdata?.answer || '';
    const plainText = this.getPlainTextForCopy(sourceText, Boolean(renderedHtml));

    try {
      await this.writeToClipboard(plainText, renderedHtml);

      this.isAnswerCopied = true;
      setTimeout(() => {
        this.isAnswerCopied = false;
      }, 2000);
    } catch (error) {
      this.loggerService.logError(this.readAloudError(error), "copyToClipboard");
    }
  }

  /**
   * Gets a sanitized HTML string from renderedAnswerHtml for clipboard use.
   */
  private getRenderedHtmlForCopy(): string {
    if (!this.renderedAnswerHtml) {
      return '';
    }
    return this.domSanitizer.sanitize(SecurityContext.HTML, this.renderedAnswerHtml) || '';
  }

  private async writeToClipboard(plainText: string, renderedHtml: string): Promise<void> {
    if (!navigator.clipboard) {
      throw new Error('Clipboard API is not supported');
    }

    const canWriteHtml = !!renderedHtml && typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function';

    if (canWriteHtml) {
      try {
        const htmlBlob = new Blob([renderedHtml], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        const item = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        await navigator.clipboard.write([item]);
        return;
      } catch {
        // Fallback to plain text clipboard below.
      }
    }

    if (typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(plainText);
      return;
    }

    throw new Error('Clipboard API writeText is not supported');
  }

  /**
   * Converts rendered HTML (preferred) or raw markdown to plain text.
   */
  private getPlainTextForCopy(content: string, isHtmlContent: boolean): string {
    let plainText = '';

    if (isHtmlContent) {
      const container = document.createElement('div');
      container.innerHTML = content.replace(/<br\s*\/?>/gi, '\n');
      plainText = (container.innerText || container.textContent || '').trim();
      return this.formatMarkdownTablesInText(plainText);
    }

    // Fallback path for raw markdown/plain text content.
    plainText = content.replace(/<br\s*\/?>/gi, '\n');
    plainText = plainText.replace(/<[^>\s]{0,20}(?:\s[^>]{0,100})?>/g, '');
    plainText = plainText
      .replace(/\*\*([^*\n]{1,100})\*\*/g, '$1')
      .replace(/__([^_\n]{1,100})__/g, '$1')
      .replace(/\*([^*\n]{1,100})\*/g, '$1')
      .replace(/_([^_\n]{1,100})_/g, '$1')
      .replace(/~~([^~\n]{1,100})~~/g, '$1')
      .replace(/`([^`\n]{1,50})`/g, '$1');

    return this.formatMarkdownTablesInText(plainText.trim());
  }

  /**
   * Converts markdown pipe table syntax into a readable ASCII table.
   * This improves copied output when the source contains raw markdown tables.
   */
  private formatMarkdownTablesInText(text: string): string {
    const lines = text.split(/\r?\n/);
    const output: string[] = [];
    let i = 0;

    while (i < lines.length) {
      if (this.isMarkdownTableHeader(lines[i])) {
        const separatorIndex = this.findNextNonEmptyLine(lines, i + 1);
        if (separatorIndex === -1 || !this.isMarkdownTableSeparator(lines[separatorIndex])) {
          output.push(lines[i]);
          i++;
          continue;
        }

        const tableBlock: string[] = [lines[i], lines[separatorIndex]];
        i = separatorIndex + 1;

        while (i < lines.length) {
          const currentLine = lines[i];
          if (!currentLine.trim()) {
            const nextNonEmptyIndex = this.findNextNonEmptyLine(lines, i + 1);
            if (nextNonEmptyIndex !== -1 && this.isPipeTableRow(lines[nextNonEmptyIndex])) {
              i = nextNonEmptyIndex;
              continue;
            }
            break;
          }

          if (this.isPipeTableRow(currentLine) || (tableBlock.length > 2 && currentLine.trim() !== '')) {
            tableBlock.push(currentLine);
            i++;
            continue;
          }
          break;
        }

        output.push(this.renderMarkdownTableBlock(tableBlock));
        continue;
      }

      output.push(lines[i]);
      i++;
    }

    return output.join('\n');
  }

  private findNextNonEmptyLine(lines: string[], fromIndex: number): number {
    for (let index = fromIndex; index < lines.length; index++) {
      if (lines[index].trim()) {
        return index;
      }
    }
    return -1;
  }

  private isMarkdownTableHeader(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.split('|').length >= 4;
  }

  private isMarkdownTableSeparator(line: string): boolean {
    const trimmed = line.trim();
    return /^\|(?:\s*:?-{3,}:?\s*\|)+$/.test(trimmed);
  }

  private isPipeTableRow(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|');
  }

  private parseMarkdownTableRow(line: string, columnCount: number): string[] {
    const trimmed = line.trim();
    const noLeading = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
    const noBorders = noLeading.endsWith('|') ? noLeading.slice(0, -1) : noLeading;
    const cells = noBorders.split('|').map(cell => cell.trim());
    const normalized = new Array(columnCount).fill('');
    for (let idx = 0; idx < columnCount; idx++) {
      normalized[idx] = cells[idx] || '';
    }
    return normalized;
  }

  private getMarkdownTableColumnCount(line: string): number {
    const trimmed = line.trim();
    const noLeading = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
    const noBorders = noLeading.endsWith('|') ? noLeading.slice(0, -1) : noLeading;
    return noBorders.split('|').length;
  }

  private renderMarkdownTableBlock(blockLines: string[]): string {
    if (blockLines.length < 2) {
      return blockLines.join('\n');
    }

    const columnCount = this.getMarkdownTableColumnCount(blockLines[0]);
    const header = this.parseMarkdownTableRow(blockLines[0], columnCount);
    const dataRows: string[][] = [];
    let activeRow: string[] | null = null;

    for (let idx = 2; idx < blockLines.length; idx++) {
      const line = blockLines[idx];
      if (this.isPipeTableRow(line)) {
        activeRow = this.parseMarkdownTableRow(line, columnCount);
        dataRows.push(activeRow);
      } else if (activeRow && line.trim()) {
        const lastColumn = columnCount - 1;
        activeRow[lastColumn] = activeRow[lastColumn]
          ? `${activeRow[lastColumn]}\n${line.trim()}`
          : line.trim();
      }
    }

    const rows = [header, ...dataRows];
    return rows
      .map(row => row.map(cell => this.normalizeTableCellWhitespace(cell)).join('\t'))
      .join('\n');
  }

  private normalizeTableCellWhitespace(cell: string): string {
    const normalizedSegments: string[] = [];
    for (const segment of cell.split('\n')) {
      const trimmed = segment.trim();
      if (trimmed) {
        normalizedSegments.push(trimmed);
      }
    }
    return normalizedSegments.join(' ');
  }

  /**
   * Removes emojis, pictographs, special symbols, and invisible characters from a given string.
   */
  removeNonResponsiveCharacters(text: string): string {
    return text
      .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\p{Symbol}]/gu, '') // Remove emojis and symbols
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces and other invisible characters
      .trim();
  }

  /**
 * Converts Roman numerals in the given text to their numeric equivalents.
 * 
 * @param text - The input text containing Roman numerals.
 * @returns The text with Roman numerals replaced by their numeric values.
 */
  convertRomanNumerals(text: string): string {
    try {
    const romanToNumber = (roman: string): number => {
      const romanNumerals: { [key: string]: number } = {
        'I': 1, 'V': 5, 'X': 10, 'L': 50, 'C': 100, 'D': 500, 'M': 1000
      };
      let total = 0;
      let prevValue = 0;
  
      for (let char of roman.toUpperCase()) {
        const value = romanNumerals[char];
        if (!value) return NaN;
        total += value;
        if (value > prevValue) {
          total -= 2 * prevValue;
        }
        prevValue = value;
      }
      return total;
    };
  
    const romanNumeralPattern = /\b[IVXLCDM]+\b/gi;
  
    return text.replace(romanNumeralPattern, (match) => {
      const num = romanToNumber(match);
      return isNaN(num) ? match : num.toString();
    });
    } catch (error) {
      this.loggerService.logError(this.readAloudError(error), "convertRomanNumerals");
      return text;
    }
  }  
}
